import type { FC, KeyboardEvent, SyntheticEvent } from 'react';
import React, { useMemo } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { ChevronDownMIcon } from '@alfalab/icons-glyph/ChevronDownMIcon';
import cn from 'classnames';

import { useAccordionContext } from '../context';
import type { TAccordionHeader, TRightIconCondition } from '../models';

export const Header: FC<TAccordionHeader> = ({
    RightIcon,
    analytics,
    children,
    id,
    panelKey,
    backgroundColor,
    headerClassNamesHelper,
    lastHeaderBottomClassNamesHelper,
    prevChildBackgroundColor,
    shadowMulticolor = true,
    styles,
    className,
    classNames = {},
    isLastChild,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const { handleToggle, iconPosition, newDesign, openedValue, theme } = useAccordionContext();

    const onClick = (event: SyntheticEvent<HTMLDivElement>) => {
        handleToggle({ event, id });
        sendEvent(analytics);
    };

    // TODO: Вынести в общие helpers
    const isA11y = (key: string) => key === 'Enter' || key === ' ';

    const onKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
        /* istanbul ignore next */
        if (!isA11y(event.key)) return;

        onClick(event);
    };

    const isOpen = openedValue?.includes(id || '');
    const rightIconCondition: TRightIconCondition = isOpen ? 'minus' : 'add';

    const headerClassNames = useMemo(
        () =>
            headerClassNamesHelper &&
            headerClassNamesHelper({
                iconPosition,
                isOpen,
                newDesign,
                shadowMulticolor,
                theme,
            }),
        [headerClassNamesHelper, iconPosition, isOpen, newDesign, shadowMulticolor, theme]
    );

    const lastHeaderBottomClassNames = useMemo(
        () => lastHeaderBottomClassNamesHelper && lastHeaderBottomClassNamesHelper(),
        [lastHeaderBottomClassNamesHelper]
    );

    return (
        <div
            className={cn(headerClassNames, className)}
            data-test-id={`accordion-header-${panelKey}`}
            onClick={onClick}
            onKeyPress={onKeyPress}
            role="button"
            style={{ backgroundColor }}
            tabIndex={panelKey}
        >
            {((theme !== 'shadow' && theme !== 'multicolor') || newDesign) && (
                <ChevronDownMIcon
                    className={cn(
                        styles.external,
                        styles.collapseIcon,
                        { [styles.newIcon]: newDesign },
                        classNames.icon
                    )}
                    fill={
                        (theme === 'dark' || theme === 'dark_y24') && newDesign
                            ? 'white'
                            : 'currentColor'
                    }
                />
            )}
            {theme === 'multicolor' ? (
                <div
                    className={styles.bgRoundFixer}
                    style={
                        prevChildBackgroundColor
                            ? { backgroundColor: prevChildBackgroundColor }
                            : undefined
                    }
                >
                    {' '}
                </div>
            ) : null}
            <div
                className={cn(
                    newDesign ? styles.newHeaderContent : styles.headerContent,
                    classNames.content
                )}
            >
                {children}
                {RightIcon && (theme === 'shadow' || theme === 'multicolor') && (
                    <div>
                        <RightIcon iconCondition={rightIconCondition} styles={styles} />
                    </div>
                )}
            </div>
            {theme === 'multicolor' && isLastChild ? (
                <div className={lastHeaderBottomClassNames || ''} style={{ backgroundColor }}></div>
            ) : null}
        </div>
    );
};
